/**
 * 对接Emap数据格式的插件
 * 使用方法： new EmapPlugin({ pagePath: '', action: '' })
 */
import { convertControl } from '@/emap-utils';
import DEFAULT_SETTINGS from './defaultSettings';
import { getModel, getData } from './service';
import { regRuleMap } from './rules';

const resolve = (from, to) => {
  const resolvedUrl = new URL(to, new URL(from, 'http://resolved'));

  if (resolvedUrl.hostname === 'resolved') {
    // `from` is a relative URL.
    const {
      pathname,
      search,
      hash
    } = resolvedUrl;
    return pathname + search + hash;
  }

  return resolvedUrl.toString();
}; // const isAbsPath = p => {
//   return p.startsWith('http://') || p.startsWith('https://')
// }


export default class EmapPlugin {
  constructor(options) {
    this.settings = Object.assign({}, DEFAULT_SETTINGS, options);
  }

  install(ctx) {
    const self = this;
    const {
      settings
    } = self; // 自定义列定义查询

    ctx.plugin('fetch-schema', async (vm, columns, cb) => {
      const {
        pagePath,
        action: actionId,
        params,
        datamodel,
        alwaysHide,
        labelPosition,
        labelWidth,
        fetchSchema
      } = settings;

      if (datamodel) {
        return datamodel;
      }

      try {
        let model = null;

        if (fetchSchema && typeof fetchSchema === 'function') {
          model = await fetchSchema({
            pagePath,
            actionId,
            type: 'form',
            params
          });
        } else {
          model = await getModel({
            pagePath,
            actionId,
            type: 'form',
            params
          });
        }

        if (model) {
          this.dataUrl = model.url;
          const result = {
            name: model.name,
            labelPosition,
            labelWidth,
            children: convertControl(model.controls.filter(item => !item['form.hidden']), {
              hides: alwaysHide
            }).map(item => {
              // emap 校验规则转换
              const {
                checkType
              } = item.options;

              if (checkType && checkType.startsWith('custom[')) {
                const regName = regRuleMap[checkType];

                if (regName) {
                  item.options.regName = regName;
                }
              }

              if (item.options && item.options.checkSize) {
                item.options.maxLength = item.options.checkSize;
              } // readonly属性转成disabled属性


              if (item.options && item.options.readonly) {
                item.options.disabled = item.options.readonly;
              } // 接收switcher的JSONParam，支持自定义值的类型


              let {
                JSONParam,
                xtype
              } = item.options;

              if (JSONParam && xtype === 'switcher') {
                try {
                  JSONParam = JSON.parse(JSONParam.replace(/'/g, '"'));
                  const {
                    activeValue,
                    inactiveValue
                  } = JSONParam;

                  if (activeValue) {
                    item.options.activeValue = activeValue;
                  }

                  if (inactiveValue !== undefined && inactiveValue !== null && inactiveValue !== '') {
                    item.options.inactiveValue = inactiveValue;
                  }
                } catch (e) {
                  console.warn('emap parse error, invalid JSONParam', JSONParam);
                }
              } // 转换emapupload参数


              if (xtype === 'cache-upload' || xtype === 'uploadfile') {
                item.xtype = 'emapupload';

                try {
                  JSONParam = JSON.parse(JSONParam.replace(/'/g, '"'));
                  const {
                    limit,
                    type,
                    listType,
                    uploadTip
                  } = JSONParam;

                  if (Number.isInteger(limit)) {
                    item.options.limit = limit;
                  }

                  if (Array.isArray(type)) {
                    item.options.type = type;
                  }

                  if (listType) {
                    item.options.listType = listType;
                  }

                  if (uploadTip) {
                    item.options.uploadTip = uploadTip;
                  }
                } catch (e) {
                  console.warn('emap parse error, invalid JSONParam', JSONParam);
                }
              }

              return item;
            })
          };
          cb(result);
        } else {
          cb(null);
        }
      } catch (e) {
        console.error(e);
        cb(null);
      }
    }); // 自定义数据查询

    ctx.plugin('fetch-data', async (vm, data, query, cb) => {
      const {
        dataUrl
      } = this;

      if (!dataUrl) {
        cb(null);
        return;
      }

      const {
        pagePath,
        action: actionId,
        beforeSend,
        fetchData
      } = settings; //const queryUrl = isAbsPath(dataUrl) ? dataUrl : resolve(pagePath, dataUrl)

      const queryUrl = resolve(pagePath, dataUrl);

      try {
        if (fetchData) {
          data = await fetchData({
            queryUrl,
            actionId,
            query
          });
        } else {
          data = await getData(queryUrl, actionId, query, beforeSend);
        }

        const rows = data && data.rows;
        cb(rows && rows[0]);
      } catch (e) {
        cb(null);
      }
    });
    ctx.plugin('format-data', (vm, data = {}) => {
      const {
        rows
      } = data || {};
      return rows || [];
    });
    ctx.plugin('custom-form-data', (vm, name, formData) => {
      const children = vm.renderSchema.children;

      for (let i = 0; i < children.length; i++) {
        const element = children[i]; // 筛选类型是select的

        if (element.options.name === name && element.xtype === 'select') {
          vm.$children[0].$children.forEach(el => {
            // 找到渲染的子组件，找到DISPLAY的值
            if (el.$attrs.prop && el.$attrs.prop === name) {
              const selectComp = el.$refs.control;

              if (selectComp) {
                const {
                  renderOptions,
                  valueKey,
                  labelKey
                } = selectComp;
                const value = formData[name];

                if (renderOptions && value != null && valueKey && labelKey) {
                  const dicData = renderOptions.find(el => el[valueKey] === value);

                  if (dicData) {
                    formData = Object.assign(formData, {
                      [`${name}_DISPLAY`]: dicData[labelKey]
                    });
                  }
                }
              }
            }
          });
          break;
        }
      }
    });
    ctx.plugin('merge-plugin-methods', vm => {
      // 添加emap-uploadfile组件的保存方法，表单中有多个组件时，用promise.all一起调用
      vm.saveEmapUpload = () => {
        // vm是data-form组件，子组件是we-form组件，we-form的子组件是Item组件
        const items = vm.$children[0].$children;
        const uploadItems = items.filter(item => item.$refs.control && item.$refs.control.saveEmapUpload);
        return Promise.all(uploadItems.map(item => item.$refs.control.saveEmapUpload()));
      }; // 添加老res表单一系列方法


      const methodsTable = {
        hideItem: ['hidden', true],
        showItem: ['hidden', false],
        requireItem: ['required', true],
        unrequireItem: ['required', false],
        disableItem: ['disabled', true],
        enableItem: ['disabled', false]
      };
      Object.keys(methodsTable).forEach(method => {
        const prop = methodsTable[method];

        vm[method] = (...args) => {
          if (typeof args[0] === 'string') {
            const name = args[0];
            vm.renderSchema.children = vm.renderSchema.children.map(item => {
              if (item.options.name === name) {
                item.options[prop[0]] = prop[1];
              }

              return item;
            });
          }

          if (Array.isArray(args[0])) {
            const names = args[0];
            vm.renderSchema.children = vm.renderSchema.children.map(item => {
              if (names.findIndex(name => item.options.name === name) > -1) {
                item.options[prop[0]] = prop[1];
              }

              return item;
            });
          }
        };
      });
    });
  }

}